<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useI18nStore } from "@/stores/i18n";
import { SUPPORTED_LOCALES, type SupportedLocale } from "@/i18n";

/**
 * Tiny dropdown next to ThemeToggle. The product is Finnish-first;
 * English exists today as a partial stub and falls back to Finnish
 * for any untranslated key — see docs/I18N.md.
 */
const { t } = useI18n();
const store = useI18nStore();

function onChange(e: Event): void {
  const next = (e.target as HTMLSelectElement).value as SupportedLocale;
  store.setLocale(next);
}
</script>

<template>
  <label class="locale-switcher" :title="t('app.locale.switchTitle')">
    <span class="icon" aria-hidden="true">🌐</span>
    <span class="visually-hidden">{{ t("app.locale.label") }}</span>
    <select :value="store.locale" @change="onChange">
      <option
        v-for="loc in SUPPORTED_LOCALES"
        :key="loc"
        :value="loc"
      >
        {{ loc.toUpperCase() }}
      </option>
    </select>
  </label>
</template>

<style scoped>
.locale-switcher {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.55rem;
  border-radius: 4px;
  font-size: 0.78rem;
  background: var(--surface, white);
  color: var(--text-muted, #6b7280);
  border: 1px solid var(--border, #d1d5db);
}
.locale-switcher:hover {
  background: var(--surface-2, #f3f4f6);
  color: var(--text-strong, #111827);
}
.locale-switcher .icon { font-size: 0.95rem; line-height: 1; }
.locale-switcher select {
  appearance: none;
  background: transparent;
  color: inherit;
  border: none;
  font: inherit;
  cursor: pointer;
  padding: 0 0.1rem;
}
.locale-switcher select:focus { outline: none; }

/* Visually hide the screen-reader-only label without `display:none`
   so assistive tech still picks it up. */
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
